import { Separator, Stack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { CodingHeader } from "./shared/CodingHeader";
import { NodeView } from "./NodeView/NodeView";
import { SpreadsheetView } from "./SpreadsheetView/SpreadsheetView";
import { CodingView } from "../../../../coding/models/CodingView";
import { LoadingScreen } from "../../../../shared/views/LoadingScreen/LoadingScreen";
import { useAutoUpdateQueryParam } from "../../hooks/useAutoUpdateQueryParam";
import { useStores } from "../../../../shared/stores/RootStore";
import { MapMode } from "../../../../project/stores/MapStore";

const CodingPageBase: React.FC = () => {
  const { codingStore, osaWayStore, osaNodeStore, osaCommentStore, mapStore } =
    useStores();

  const [initialLoadingDone, setInitialLoadingDone] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    codingStore.setView(
      CodingView.fromString(searchParams.get(CodingView.param) ?? ""),
    );
    mapStore.setMode(MapMode.CraCoding);
    return () => mapStore.setMode(MapMode.ProjectPage);
    //eslint-disable-next-line
  }, []);

  useAutoUpdateQueryParam(CodingView.param, codingStore.view, setSearchParams);

  const selectedOsaWay = osaWayStore.getSelected();
  const selectedNode = osaNodeStore.getSelected();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!codingStore.changesSaved) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      codingStore.stopAutoSaveTimer();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setInitialLoadingDone(false);
    codingStore.resetState();

    const onRouteChange = (osaWayId: string) => {
      Promise.all([
        codingStore.fetchAttributesByOsaWayId(osaWayId),
        codingStore.fetchLatestCodingChange(osaWayId),
        osaCommentStore.fetchComments(osaWayId),
      ]).then(() => {
        setInitialLoadingDone(true);
        codingStore.startAutoSaveTimer();
      });
    };

    if (selectedOsaWay) {
      onRouteChange(selectedOsaWay.id);
    }

    return () => {
      if (!codingStore.changesSaved) {
        codingStore.onUnloadSaveChanges();
      }
    };
    //eslint-disable-next-line
  }, [selectedOsaWay?.id]);

  useEffect(() => {
    if (selectedNode && initialLoadingDone) {
      codingStore.suggestionEngine.suggestGradeType(
        selectedNode.id,
        selectedNode.slope,
      );

      if (codingStore.view !== CodingView.Enum.Spreadsheet) {
        codingStore.copyFromPreviousNode();
      }
    }

    //eslint-disable-next-line
  }, [selectedNode, initialLoadingDone]);

  useEffect(() => {
    const refreshWay = async () => {
      if (!selectedNode && selectedOsaWay?.id) {
        await osaWayStore.refreshOsaWay(selectedOsaWay.id);
      }
    };
    refreshWay();
    // eslint-disable-next-line
  }, [selectedNode]);

  return (
    <Stack
      flexGrow={1}
      overflow="auto"
      position={"relative"}
      separator={<Separator alignSelf={"stretch"} />}
    >
      {initialLoadingDone ? (
        <>
          <CodingHeader />
          <Separator />

          {codingStore.view === CodingView.Enum.Spreadsheet ? (
            <SpreadsheetView />
          ) : (
            <NodeView />
          )}
        </>
      ) : (
        <LoadingScreen />
      )}
    </Stack>
  );
};

export const CodingPage = observer(CodingPageBase);
